import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, TrendingUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoginArea } from '@/components/auth/LoginArea';
import { SuggestedAccounts } from '@/components/discovery/SuggestedAccounts';
import { useCurrentUser } from '@/hooks/useCurrentUser';

export function RightSidebar() {
  const [searchVal, setSearchVal] = useState('');
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchVal.trim()) return;
    navigate(`/explore?q=${encodeURIComponent(searchVal.trim())}`);
  };

  const trendingHashtags = [
    { tag: 'nostr', count: '14.2K posts', category: 'Technology · Trending' },
    { tag: 'bitcoin', count: '85.4K posts', category: 'Finance · Trending' },
    { tag: 'shakespeare', count: '5.1K posts', category: 'Development · Trending' },
    { tag: 'ai', count: '124.9K posts', category: 'Science · Trending' },
    { tag: 'web3', count: '22.3K posts', category: 'Gaming · Trending' },
  ];

  return (
    <aside className="hidden lg:flex flex-col gap-4 w-[350px] shrink-0 sticky top-0 h-screen py-3 pl-4 pr-2 select-none overflow-y-auto overflow-x-hidden border-l border-border">
      {/* Search Input */}
      <form onSubmit={handleSearchSubmit} className="relative w-full">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground size-4" />
        <Input
          type="text"
          placeholder="Search"
          className="w-full pl-11 bg-secondary/40 border-none rounded-full h-11 focus-visible:bg-background focus-visible:ring-1 focus-visible:ring-sky-500 text-[15px]"
          value={searchVal}
          onChange={(e) => setSearchVal(e.target.value)}
        />
      </form>

      {/* Account Login area if not logged in */}
      {!user && (
        <Card className="rounded-2xl border border-border/80 shadow-xs bg-slate-500/5 p-1 relative overflow-hidden">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-lg font-extrabold leading-tight">New to Twittstr?</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 text-sm text-muted-foreground flex flex-col gap-3">
            <p className="text-xs leading-normal opacity-90">Sign up or connect now to secure your decentralized key pairs and start microblogging!</p>
            <LoginArea className="w-full" />
          </CardContent>
        </Card>
      )}

      {/* What's Happening */}
      <Card className="rounded-2xl border border-border/80 overflow-hidden shadow-none bg-secondary/20">
        <CardHeader className="p-4 pb-3">
          <CardTitle className="text-xl font-extrabold font-sans">What's happening</CardTitle>
        </CardHeader>
        <div className="flex flex-col">
          {trendingHashtags.map((item, index) => (
            <button
              key={index}
              onClick={() => navigate(`/explore?q=${encodeURIComponent('#' + item.tag)}`)}
              className="text-left py-3 px-4 hover:bg-secondary/40 transition-colors w-full cursor-pointer flex flex-col gap-0.5 leading-normal"
            >
              <span className="text-[13px] text-muted-foreground">{item.category}</span>
              <span className="font-extrabold text-[15px]">#{item.tag}</span>
              <span className="text-xs text-muted-foreground">{item.count}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* Suggested Accounts - only show when logged in */}
      {user && <SuggestedAccounts maxAccounts={3} />}

      {/* Vibed with Shakespeare */}
      <div className="py-2 px-4 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 leading-normal">
        <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1 font-semibold text-sky-500">
          Vibed with Shakespeare
        </a>
        <span>·</span>
        <span>© 2026 Twittstr</span>
        <span>·</span>
        <a href="https://nostrhub.io/naddr1qvzqqqrhnypzp8mwdemz3fsenhjchhhd2j2257txg7jdwxrn95n5989vadewra3qqyd8wumn8ghj7emfwsh8x6rpddjhxur9v9ex2tnyd9uj7qqg23mkjar5wd68ym2atwx" target="_blank" rel="noopener noreferrer" className="hover:underline font-semibold text-sky-500">
          Fork Me!
        </a>
      </div>
    </aside>
  );
}
